import { motion } from "framer-motion";
import { ExternalLink, Palette, TrendingUp, Crown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ApplyCta from "./ApplyCta";

// Import case images
import duomo1Img from "@/assets/cases/duomo-1.png";
import duomo2Img from "@/assets/cases/duomo-2.png";
import realEstateImg from "@/assets/cases/real-estate.png";
import silvanaVideoImg from "@/assets/cases/silvana-video.png";

interface LinkItem {
  label: string;
  url: string;
  image: string;
}

const LinkPreviewCard = ({ link }: { link: LinkItem }) => (
  <a
    href={link.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group/card block overflow-hidden rounded-xl border border-border bg-muted/30 hover:border-primary/50 hover:shadow-lg transition-all duration-300"
  >
    <div className="relative aspect-[4/3] bg-gradient-to-br from-muted to-muted/50 overflow-hidden">
      <img
        src={link.image}
        alt={link.label}
        className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-primary/0 group-hover/card:bg-primary/10 transition-colors duration-300 flex items-center justify-center">
        <ExternalLink className="w-8 h-8 text-white opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
      </div>
    </div>
    <div className="p-3 flex items-center justify-between">
      <span className="text-sm font-medium text-foreground">{link.label}</span>
      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover/card:text-primary transition-colors" />
    </div>
  </a>
);

const examples = [
  {
    icon: Palette,
    category: "IDENTIDAD Y REBRANDING",
    title: "DUOMO Helados",
    subtitle: "Más de 30 años de historia elevados a un ecosistema moderno",
    description:
      "Redefinimos su experiencia completa: Estrategia, Identidad, Packaging, RETAIL Comercial teniendo en cuenta la implementación en más de 70 locales. Comunicación y todos los puntos de contacto. Todo manteniendo intacta su esencia.",
    links: [
      { label: "Ver proyecto", url: "https://www.instagram.com/p/DSFVuDbDv8d/?img_index=1", image: duomo1Img },
      { label: "Ver más", url: "https://www.instagram.com/p/DSFtkGpDZfW/?img_index=1", image: duomo2Img },
    ],
    stats: [
      { label: "Años de historia", value: "30+" },
      { label: "Locales implementados", value: "70+" },
    ],
    gradient: "from-[hsl(216_38%_33%)] to-[hsl(216_32%_52%)]",
  },
  {
    icon: Crown,
    category: "MARCA PERSONAL · REAL ESTATE",
    title: "Una mirada al futuro | Real Estate Silvana Ratti",
    subtitle: "Transformamos la empresa de Silvana Ratti a través de un rebranding integral que redefine su visión de futuro",
    description:
      "Mediante una arquitectura de marca sólida y una narrativa con propósito, elevamos su posicionamiento en la industria del diseño y la construcción.",
    links: [
      { label: "Ver empresa constructora", url: "https://silvanaratti.com/", image: realEstateImg },
      { label: "Ver video del proyecto", url: "https://youtube.com/watch?v=gS--bws9FAg", image: silvanaVideoImg },
    ],
    results: [
      "Identidad: Diseñamos todo el ecosistema sistema visual y verbal.",
      "Marca Personal: Potenciamos el liderazgo de Silvana como referente absoluta del sector, proyectando su influencia.",
      "Estructuramos nuevas unidades de negocio bajo una identidad coherente que maximiza el valor de marca.",
    ],
    scope: "Alcance del proyecto: Consultoría, Rebranding, Estrategia y Arquitectura de Marca, Narrativa, Identidad Visual/Verbal y Activación.",
    gradient: "from-[hsl(216_38%_33%)] to-[hsl(216_32%_52%)]",
  },
];

const ServiceExamples = () => {
  return (
    <section id="casos-reales" className="py-20 md:py-28 relative overflow-hidden bg-background">
      <div className="container px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium tracking-widest mb-6">
            CASOS REALES
          </span>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Marcas que confiaron en nosotros para transformar su presencia y alcanzar nuevos niveles
            de autoridad.
          </p>
        </motion.div>

        {/* Collapsible Examples */}
        <div className="max-w-5xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {examples.map((example, index) => (
              <motion.div
                key={example.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <AccordionItem
                  value={`case-${index}`}
                  className="rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all duration-500 data-[state=open]:shadow-xl"
                >
                  <AccordionTrigger className="px-6 md:px-10 py-6 hover:no-underline">
                    <div className="flex items-center gap-4 text-left">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-br ${example.gradient} flex items-center justify-center shrink-0 shadow-lg`}
                      >
                        <example.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <span className="text-xs font-semibold tracking-widest text-primary uppercase block mb-1">
                          {example.category}
                        </span>
                        <h3 className="font-display text-xl md:text-2xl font-bold text-foreground">
                          {example.title}
                        </h3>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-6 md:px-10 pb-8">
                    <p className="text-primary font-medium mb-3">{example.subtitle}</p>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {example.description}
                    </p>

                    {/* Stats */}
                    {example.stats && (
                      <div className="flex gap-8 mb-6">
                        {example.stats.map((stat) => (
                          <div key={stat.label}>
                            <div className="text-3xl font-bold text-gradient-gold">{stat.value}</div>
                            <div className="text-sm text-muted-foreground">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Results */}
                    {example.results && (
                      <ul className="space-y-2 mb-6">
                        {example.results.map((result) => (
                          <li key={result} className="flex items-start gap-2 text-sm">
                            <TrendingUp className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                            <span className="text-foreground">{result}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Scope */}
                    {example.scope && (
                      <p className="text-sm text-muted-foreground italic mb-6 border-l-2 border-primary/30 pl-4">
                        {example.scope}
                      </p>
                    )}

                    {/* Link Previews */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {example.links.map((link) => (
                        <LinkPreviewCard key={link.label} link={link} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>
        <ApplyCta />
      </div>
    </section>
  );
};

export default ServiceExamples;
